// Dependency-free page metadata (title + description + canonical + robots +
// Open Graph/Twitter). Called from the router on navigation, and from dynamic
// pages (e.g. product) once their data has loaded.
//
// The expected runtime state is: every key written here already exists as a
// static placeholder in `index.html`, so this function overwrites in place
// and adjacent OG / Twitter tags stay grouped together. The insertNear()
// fallback handles the rare case where a tag isn't pre-declared (e.g. a
// brand-new key added in code but not yet in the HTML) — it splices the new
// node next to its closest sibling instead of appending to the end of head.

const BRAND = 'KeyzoStore';
const DEFAULT_TITLE = `${BRAND} — Game Keys Marketplace`;
const DEFAULT_DESCRIPTION =
  'Buy game keys instantly — Steam, PlayStation, Xbox, Nintendo Switch, Epic Games and GOG. ' +
  'Verified keys from authorized distributors, delivered the moment you pay.';
// Branded social-share card bundled at frontend/public/og-default.png. Used
// when a page doesn't supply its own image (landing, catalog list, account
// pages) so previews still render with the brand instead of a blank box.
const DEFAULT_OG_IMAGE = '/og-default.png';

export interface PageMeta {
  /** Page title without the brand suffix (the suffix is added automatically). */
  title?: string;
  description?: string;
  /** Absolute URL or root-relative path for the social share image. */
  image?: string | null;
  /**
   * Whether search engines may index this page. Defaults to true. Set false on
   * private (auth-gated), auth-form and error pages → `noindex, nofollow`.
   */
  index?: boolean;
  /**
   * Canonical URL — absolute, or root-relative path resolved against the
   * origin. Defaults to the current path (query string stripped).
   */
  canonical?: string;
}

/**
 * If a meta tag doesn't exist yet, insert it after the closest sibling of
 * the same family (og:*, twitter:*, generic name=*) rather than appending to
 * the end of <head>. Keeps related tags clustered next to the static ones in
 * index.html so the source view stays tidy.
 */
function insertNear(newEl: HTMLMetaElement, key: 'name' | 'property', value: string): void {
  const head = document.head;
  const family = value.includes(':') ? value.split(':', 1)[0] : '';
  const candidates: Element[] = [];
  if (family) {
    candidates.push(
      ...Array.from(head.querySelectorAll(`meta[${key}^="${family}:"]`)),
    );
  }
  if (candidates.length === 0) {
    candidates.push(...Array.from(head.querySelectorAll(`meta[${key}]`)));
  }
  const anchor = candidates[candidates.length - 1];
  if (anchor && anchor.parentNode) {
    anchor.parentNode.insertBefore(newEl, anchor.nextSibling);
  } else {
    head.appendChild(newEl);
  }
}

function upsertMeta(key: 'name' | 'property', value: string, content: string): void {
  const selector = `meta[${key}="${value}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, value);
    insertNear(el, key, value);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function toAbsolute(value: string): string {
  if (typeof window === 'undefined') return value;
  return /^https?:\/\//.test(value) ? value : `${window.location.origin}${value}`;
}

/**
 * Site-wide values from the admin Meta / SEO page (GET /api/meta). Blank
 * fields keep the built-in defaults above. Applied client-side only — the
 * static tags in index.html are what non-JS crawlers see. TODO(owner): server-side head injection (TD-01, README).
 */
export interface SiteMeta {
  title?: string;
  description?: string;
  siteName?: string;
  image?: string;
  imageAlt?: string;
  twitterCard?: string;
  icons?: Array<{ rel: string; sizes: string; href: string }>;
}

let site: SiteMeta = {};
let lastPage: PageMeta = {};

/** Store the admin's site meta, swap favicons, and re-apply the current page. */
export function applySiteMeta(next: SiteMeta): void {
  site = next;
  if (next.icons?.length) {
    document.head.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((el) => el.remove());
    for (const icon of next.icons) {
      const link = document.createElement('link');
      link.rel = icon.rel;
      link.setAttribute('sizes', icon.sizes);
      link.href = icon.href;
      document.head.appendChild(link);
    }
  }
  setPageMeta(lastPage);
}

export function setPageMeta(meta: PageMeta = {}): void {
  lastPage = meta;
  const brand = site.siteName || BRAND;
  const title = meta.title ? `${meta.title} — ${brand}` : site.title || DEFAULT_TITLE;
  const description = (meta.description ?? '').trim() || site.description || DEFAULT_DESCRIPTION;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  // Canonical drops query/hash so filtered/paginated views collapse to one URL.
  const canonical = meta.canonical ? toAbsolute(meta.canonical) : `${origin}${path}`;
  const indexable = meta.index !== false;
  // Pages that don't pass a per-page image fall back to the branded card.
  // Crawlers that ignore JS still see the static og:image in index.html;
  // this branch keeps the shown card consistent for in-app SPA navigations.
  const image = meta.image ?? (site.image || DEFAULT_OG_IMAGE);
  const absImage = toAbsolute(image);

  document.title = title;
  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', indexable ? 'index, follow' : 'noindex, nofollow');
  upsertLink('canonical', canonical);

  // Open Graph
  upsertMeta('property', 'og:site_name', brand);
  upsertMeta('property', 'og:type', meta.image ? 'product' : 'website');
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', absImage);
  if (site.imageAlt && !meta.image) upsertMeta('property', 'og:image:alt', site.imageAlt);

  // Twitter — use summary_large_image throughout so the branded fallback
  // renders at full width even when a page doesn't ship a bespoke image.
  upsertMeta('name', 'twitter:card', site.twitterCard || 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', absImage);
}
